import React, { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import cities from '../../data/cities';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCities?: any[];
  onAddCity?: (city: any) => void;
  getLocalizedCityName?: (city: any) => string;
}

interface City {
  name: string;
  timezone: string;
  timezoneName: string;
  nameFr?: string;
  nameEs?: string;
  nameDe?: string;
  namePt?: string;
  nameZh?: string;
}

export function CommandPalette({ 
  isOpen, 
  onClose, 
  selectedCities = [], 
  onAddCity,
  getLocalizedCityName
}: CommandPaletteProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Filter cities based on search query and exclude already selected ones
  const filteredCities = cities.filter(city => {
    // Check if the city name includes the search term (case insensitive)
    const cityName = getLocalizedCityName ? getLocalizedCityName(city) : city.name;
    const matchesSearch = cityName.toLowerCase().includes(query.toLowerCase()) ||
                         city.timezoneName.toLowerCase().includes(query.toLowerCase());
    
    // Check if the city is already selected
    const isAlreadySelected = selectedCities.some(selectedCity => 
      selectedCity.name === city.name
    );
    
    return matchesSearch && !isAlreadySelected;
  }).slice(0, 10); // Limit to 10 results for performance

  const handleAddCity = (city: City) => {
    if (onAddCity) {
      onAddCity(city);
    }
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < filteredCities.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : filteredCities.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredCities[selectedIndex]) {
            handleAddCity(filteredCities[selectedIndex]);
          }
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredCities, selectedIndex, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="flex items-start justify-center pt-[10vh] px-4">
        <div 
          className="w-full max-w-lg bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search input */}
          <div className="flex items-center px-4 py-3 border-b border-gray-200">
            <MapPin className="w-5 h-5 text-gray-400 mr-3" />
            <input
              type="text"
              placeholder={t('commandPalette.placeholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 outline-none text-gray-900 placeholder-gray-500"
              autoFocus
            />
          </div>

          {/* Cities list */}
          <div className="max-h-96 overflow-y-auto py-2">
            {filteredCities.length === 0 ? (
              <div className="px-4 py-3 text-gray-500 text-center">
                {query ? t('commandPalette.noResults') : t('commandPalette.startTyping')}
              </div>
            ) : (
              filteredCities.map((city, index) => (
                <button
                  key={city.name}
                  onClick={() => handleAddCity(city)}
                  className={`
                    w-full flex items-center px-4 py-3 text-left hover:bg-gray-50 transition-colors
                    ${index === selectedIndex ? 'bg-gray-100' : ''}
                  `}
                >
                  <MapPin className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {getLocalizedCityName ? getLocalizedCityName(city) : city.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {city.timezoneName} • {city.timezone}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between text-xs text-gray-500">
              <span>{t('commandPalette.navigateInstructions')}</span>
              <span>{t('commandPalette.selectInstruction')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}